import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import ScrollReveal from "./ScrollReveal";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

import foto01 from "@/assets/espaco-novo/foto-01.webp";
import foto02 from "@/assets/espaco-novo/foto-02.webp";
import foto03 from "@/assets/espaco-novo/foto-03.webp";
import foto04 from "@/assets/espaco-novo/foto-04.webp";
import foto05 from "@/assets/espaco-novo/foto-05.webp";
import foto06 from "@/assets/espaco-novo/foto-06.webp";
import foto07 from "@/assets/espaco-novo/foto-07.webp";
import foto08 from "@/assets/espaco-novo/foto-08.webp";
import foto09 from "@/assets/espaco-novo/foto-09.webp";
import foto10 from "@/assets/espaco-novo/foto-10.webp";
import foto11 from "@/assets/espaco-novo/foto-11.webp";
import foto12 from "@/assets/espaco-novo/foto-12.webp";
import foto13 from "@/assets/espaco-novo/foto-13.webp";
import foto14 from "@/assets/espaco-novo/foto-14.webp";
import foto15 from "@/assets/espaco-novo/foto-15.webp";

const SITE = "https://www.equilybra.com.br";

// Alt e caption otimizados para Google Imagens — descrevem o que está na foto
// com palavras-chave reais (clínica de psicologia, Ipiranga, consultório,
// psicoterapia, avaliação neuropsicológica, sala infantil, etc.).
const slides = [
  {
    id: 1,
    src: foto01,
    alt: "Sessão de psicoterapia adulto na clínica Equilybra Psicologia — Ipiranga, São Paulo",
    caption: "Consultório de psicoterapia adulto",
    portrait: false,
  },
  {
    id: 2,
    src: foto02,
    alt: "Recepção da clínica Equilybra Psicologia com logotipo e sofá — Ipiranga, SP",
    caption: "Recepção da clínica Equilybra",
    portrait: false,
  },
  {
    id: 3,
    src: foto03,
    alt: "Sala de psicologia infantil com psicóloga e criança em atividade lúdica — Equilybra Ipiranga",
    caption: "Sala de psicologia infantil",
    portrait: false,
  },
  {
    id: 4,
    src: foto04,
    alt: "Consultório de psicoterapia adulto na clínica Equilybra Psicologia — atendimento humanizado",
    caption: "Consultório de psicoterapia individual",
    portrait: false,
  },
  {
    id: 5,
    src: foto05,
    alt: "Escadaria da clínica Equilybra Psicologia com quadro inspiracional — Ipiranga, São Paulo",
    caption: "Área interna da clínica Equilybra",
    portrait: true,
  },
  {
    id: 6,
    src: foto06,
    alt: "Família chegando para atendimento familiar na clínica Equilybra Psicologia — Ipiranga, SP",
    caption: "Atendimento familiar na Equilybra",
    portrait: true,
  },
  {
    id: 7,
    src: foto07,
    alt: "Consultório de terapia de casal com poltronas roxas — Equilybra Psicologia, Ipiranga",
    caption: "Sala de psicoterapia de casal",
    portrait: false,
  },
  {
    id: 8,
    src: foto08,
    alt: "Sala de atendimento com poltronas azuis — clínica de psicologia Equilybra, Ipiranga SP",
    caption: "Sala de atendimento psicológico",
    portrait: false,
  },
  {
    id: 9,
    src: foto09,
    alt: "Sala infantil da Equilybra com mesa de atividades para psicoterapia com crianças",
    caption: "Sala de psicoterapia infantil",
    portrait: false,
  },
  {
    id: 10,
    src: foto10,
    alt: "Atendimento psicológico para melhor idade na clínica Equilybra — Ipiranga, São Paulo",
    caption: "Atendimento para melhor idade",
    portrait: false,
  },
  {
    id: 11,
    src: foto11,
    alt: "Família na recepção infantil da clínica Equilybra Psicologia — Ipiranga, SP",
    caption: "Recepção infantil e familiar",
    portrait: false,
  },
  {
    id: 12,
    src: foto12,
    alt: "Equipe administrativa da Equilybra Psicologia em atendimento — clínica no Ipiranga",
    caption: "Equipe administrativa Equilybra",
    portrait: false,
  },
  {
    id: 13,
    src: foto13,
    alt: "Corredor de consultórios da clínica Equilybra Psicologia — Ipiranga, São Paulo",
    caption: "Corredor de consultórios da clínica",
    portrait: true,
  },
  {
    id: 14,
    src: foto14,
    alt: "Avaliação psicológica infantil com psicóloga e criança desenhando — Equilybra Ipiranga",
    caption: "Sala de avaliação psicológica infantil",
    portrait: true,
  },
  {
    id: 15,
    src: foto15,
    alt: "Fachada da clínica Equilybra Psicologia com placa iluminada — Rua Agostinho Gomes, Ipiranga, SP",
    caption: "Fachada da clínica Equilybra no Ipiranga",
    portrait: true,
  },
];

const PhotoCarousel = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  // JSON-LD ImageGallery + ImageObject por foto — ajuda Google Imagens a
  // indexar cada foto com legenda, autor e licença.
  const imageGallerySchema = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    name: "Espaço da Clínica Equilybra Psicologia — Ipiranga, São Paulo",
    description:
      "Fotos oficiais do consultório e recepção da Equilybra Psicologia: salas de psicoterapia, sala infantil, sala de casal, sala de avaliação neuropsicológica e fachada.",
    url: `${SITE}/#nossa-clinica`,
    image: slides.map((s) => ({
      "@type": "ImageObject",
      contentUrl: `${SITE}${s.src}`,
      url: `${SITE}${s.src}`,
      name: s.caption,
      caption: s.alt,
      description: s.alt,
      representativeOfPage: s.id === 1,
      creditText: "Equilybra Psicologia",
      creator: { "@type": "Organization", name: "Equilybra Psicologia" },
      copyrightNotice: "© Equilybra Psicologia",
      license: `${SITE}/politica-privacidade`,
      acquireLicensePage: `${SITE}/contato`,
      contentLocation: {
        "@type": "Place",
        name: "Equilybra Psicologia — Ipiranga, São Paulo",
      },
    })),
  };

  return (
    <section
      id="nossa-clinica"
      className="py-24 bg-section-soft relative overflow-hidden"
      aria-label="Fotos da clínica Equilybra Psicologia"
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(imageGallerySchema)}
        </script>
      </Helmet>

      <div className="absolute top-0 left-0 right-0 section-glow-divider" />
      <div className="absolute bottom-0 left-0 right-0 section-glow-divider" />

      <div className="container mx-auto px-4">
        <ScrollReveal className="text-center mb-12">
          <p className="text-primary font-semibold uppercase tracking-[0.2em] text-sm mb-3">
            Nossa Clínica
          </p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground">
            Conheça Nosso Espaço
          </h2>
          <div className="w-16 h-1 mx-auto mt-4 rounded-full bg-gradient-to-r from-primary to-accent" />
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <div className="relative max-w-4xl mx-auto">
            <Carousel
              opts={{ loop: true, align: "center" }}
              setApi={setApi}
              className="w-full"
            >
              <CarouselContent>
                {slides.map((slide, i) => (
                  <CarouselItem key={slide.id}>
                    <figure
                      className={`relative rounded-3xl overflow-hidden bg-card shadow-soft border border-border glow-primary transition-all duration-500 ${
                        slide.portrait ? "aspect-[9/16] max-w-sm mx-auto" : "aspect-[16/9]"
                      }`}
                    >
                      <motion.img
                        src={slide.src}
                        alt={slide.alt}
                        title={slide.caption}
                        width={slide.portrait ? 720 : 1200}
                        height={slide.portrait ? 1280 : 675}
                        initial={{ opacity: 0, scale: 1.02 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
                        className="absolute inset-0 w-full h-full object-contain bg-muted"
                        loading={i === 0 ? "eager" : "lazy"}
                        decoding="async"
                        fetchPriority={i === 0 ? "high" : "auto"}
                      />
                      <figcaption className="sr-only">{slide.caption}</figcaption>
                    </figure>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious
                aria-label="Foto anterior da clínica Equilybra"
                className="left-3 h-12 w-12 rounded-2xl glass shadow-card border-0 hover:scale-110 transition-transform"
              />
              <CarouselNext
                aria-label="Próxima foto da clínica Equilybra"
                className="right-3 h-12 w-12 rounded-2xl glass shadow-card border-0 hover:scale-110 transition-transform"
              />
            </Carousel>

            {/* Legenda visível da foto atual — reforça contexto pra usuários e crawlers */}
            <p className="text-center text-sm text-muted-foreground mt-6 font-medium">
              {slides[current]?.caption}
            </p>

            <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => api?.scrollTo(i)}
                  className={`h-2.5 rounded-full transition-all duration-500 ${
                    i === current
                      ? "bg-primary w-8"
                      : "bg-border w-2.5 hover:bg-muted-foreground/30"
                  }`}
                  aria-label={`Ir para foto ${i + 1} — ${slides[i].caption}`}
                />
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default PhotoCarousel;
